/**
 * FinalisePanel — freezes a completed assessment into an immutable deliverable.
 * Before finalising, exports render live (dev/draft). After finalising, every
 * export renders from a frozen snapshot so the report can never silently change.
 * Re-finalising supersedes the snapshot with a new version, archiving the old.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Lock, ShieldCheck, RefreshCw, History, FileDown, BadgeCheck, ListChecks, X, FileLock2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "../../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const FMT_LABEL = { pdf: "Full assessment", execpack: "Executive Decision Pack", boardpaper: "Board Decision Paper" };

export default function FinalisePanel({ runId, run, onChange }) {
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [verify, setVerify] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [issued, setIssued] = useState([]);
  const [issuedVerify, setIssuedVerify] = useState(null);
  const [issuedBusy, setIssuedBusy] = useState(false);
  const finalised = !!run?.finalised;
  const meta = run?.frozen_meta;
  const curVersion = meta?.version;

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/frozen-history`);
      setHistory(data.items || []);
    } catch { /* noop */ }
  }, [runId]);

  const loadIssued = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/as-issued`);
      setIssued(data.items || []);
    } catch { /* noop */ }
  }, [runId]);

  useEffect(() => { if (finalised) { loadHistory(); loadIssued(); } }, [finalised, loadHistory, loadIssued]);

  const finalise = async () => {
    if (!window.confirm("Finalise & freeze this report? Exports will be locked to this snapshot. You can re-finalise later to supersede it.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/finalise`);
      toast.success(`Report finalised — frozen as v${data.version}`);
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not finalise");
    } finally { setBusy(false); }
  };

  const refinalise = async () => {
    if (!window.confirm("Re-finalise creates a NEW frozen version from the current data and supersedes the existing one (kept on record). Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/refinalise`);
      toast.success(`Re-finalised — now frozen as v${data.version} (superseded v${data.superseded_version})`);
      onChange?.();
      loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not re-finalise");
    } finally { setBusy(false); }
  };

  const isCompleted = run?.status === "completed";

  const runVerify = async () => {
    setVerifying(true);
    setVerify(null);
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/verify-exports`);
      setVerify(data);
      if (data.all_ok) toast.success("All deliverable formats render cleanly");
      else toast.error("Some formats failed — see details");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setVerifying(false); }
  };

  const downloadProgress = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/assessments/runs/${runId}/export/progress`, { credentials: "include" });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : "progress-report.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Progress report downloaded");
    } catch (err) {
      toast.error(err?.message || "Could not download progress report");
    }
  };

  const issuedForVersion = issued.filter((a) => a.version === curVersion);

  const captureIssued = async () => {
    setIssuedBusy(true);
    try {
      await api.post(`/admin/assessments/runs/${runId}/as-issued/capture`);
      toast.success("As-issued copies captured");
      await loadIssued();
      setIssuedVerify(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not capture as-issued copies");
    } finally { setIssuedBusy(false); }
  };

  const verifyIssued = async () => {
    setIssuedBusy(true);
    setIssuedVerify(null);
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/as-issued/verify`, { params: { version: curVersion } });
      setIssuedVerify(data);
      if (data.all_ok) toast.success("Integrity verified — bytes match the issued copies");
      else toast.error("Integrity check failed for one or more copies");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not verify integrity");
    } finally { setIssuedBusy(false); }
  };

  const downloadIssued = async (fmt) => {
    try {
      const res = await fetch(`${API_BASE}/admin/assessments/runs/${runId}/as-issued/${fmt}/download?version=${curVersion}`, { credentials: "include" });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const verified = res.headers.get("x-integrity-verified") === "true";
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : `as-issued-${fmt}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      if (verified) toast.success("As-issued copy downloaded — integrity verified");
      else toast.warning("Downloaded, but integrity check did NOT match the recorded hash");
    } catch (err) {
      toast.error(err?.message || "Could not download as-issued copy");
    }
  };

  return (
    <div className={`border p-5 ${finalised ? "bg-[#0B0B0C] text-white border-black" : "bg-white border-zinc-200"}`} data-testid="finalise-panel">
      <div className="flex items-center gap-2 text-[12px] font-medium uppercase tracking-[0.16em]">
        {finalised ? <Lock size={14} className="text-[#A179FA]"/> : <ShieldCheck size={14} className="text-[#7737F6]"/>}
        <span className={finalised ? "text-zinc-200" : "text-zinc-700"}>Report finalisation</span>
      </div>

      <div className={`mt-3 border p-2.5 ${finalised ? "border-zinc-700" : "border-zinc-200 bg-zinc-50"}`} data-testid="verify-exports-block">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-[11px] flex items-center gap-1.5 ${finalised ? "text-zinc-300" : "text-zinc-600"}`}><ListChecks size={13}/> Export integrity</span>
          <button type="button" onClick={runVerify} disabled={verifying} className={`text-[11px] px-2 py-1 border disabled:opacity-50 ${finalised ? "border-zinc-600 text-zinc-200 hover:bg-white/10" : "border-zinc-300 text-zinc-700 hover:bg-white"}`} data-testid="verify-exports-btn">
            {verifying ? "Checking…" : "Verify all formats"}
          </button>
        </div>
        {verify && (
          <div className="mt-2 space-y-1" data-testid="verify-exports-result">
            <div className={`text-[11px] font-medium flex items-center gap-1.5 ${verify.all_ok ? "text-emerald-600" : "text-red-500"}`}>
              {verify.all_ok ? <BadgeCheck size={13}/> : <X size={13}/>}
              {verify.all_ok ? "All formats render cleanly" : "Some formats failed"}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {verify.results.map((r) => (
                <span key={r.format} title={r.ok ? `${Math.round(r.size/1024)} KB` : r.error} className={`text-[10px] px-1.5 py-0.5 ${r.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid={`verify-fmt-${r.format}`}>
                  {r.ok ? "✓" : "✕"} {r.format}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {!finalised ? (
        <>
          <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
            Freezing snapshots every input the deliverable renders from (scores, framework, decisions, value-at-stake, roster, logo) so the report is immutable. Action progress is tracked separately and never alters the baseline.
          </p>
          <button type="button" onClick={finalise} disabled={busy || !isCompleted || !verify?.all_ok} className="ek-btn ek-btn-primary w-full mt-4 text-[13px] disabled:opacity-50" data-testid="finalise-btn">
            <Lock size={13}/> {busy ? "Finalising…" : "Finalise & freeze report"}
          </button>
          {!isCompleted && <p className="text-[10.5px] text-amber-700 mt-2">Mark the run complete before finalising.</p>}
          {isCompleted && !verify?.all_ok && <p className="text-[10.5px] text-amber-700 mt-2" data-testid="finalise-verify-hint">Run “Verify all formats” above — it must pass before you can freeze.</p>}
        </>
      ) : (
        <>
          <div className="mt-3 text-[12px] text-zinc-300" data-testid="finalise-status">
            <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[#A179FA] bg-[#7737F6]/20 px-2 py-0.5">Frozen · v{meta?.version}</span>
            <div className="mt-2">Finalised {fmtDate(meta?.frozen_at)}{meta?.finalised_by ? ` · ${meta.finalised_by}` : ""}</div>
            <div className="mt-1 text-zinc-400 text-[11px]">Exports now render from this immutable snapshot.</div>
          </div>
          <button type="button" onClick={refinalise} disabled={busy} className="w-full mt-4 text-[13px] border border-[#7737F6] text-[#C0A8F8] hover:bg-[#7737F6]/20 px-3 py-2 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="refinalise-btn">
            <RefreshCw size={13}/> {busy ? "Working…" : "Re-finalise (new version)"}
          </button>
          <button type="button" onClick={downloadProgress} className="w-full mt-2 text-[13px] bg-[#7737F6] hover:bg-[#5D28C7] text-white px-3 py-2 flex items-center justify-center gap-2" data-testid="progress-report-btn">
            <FileDown size={13}/> Download progress report (PDF)
          </button>
          <p className="text-[10.5px] text-zinc-400 mt-1.5">Overlays live action progress onto the frozen baseline — the deliverable itself never changes.</p>

          <div className="mt-4 border border-zinc-700 p-2.5 scroll-mt-24" data-testid="as-issued-block">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] flex items-center gap-1.5 text-zinc-300"><FileLock2 size={13}/> As-issued copies · v{curVersion}</span>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={verifyIssued} disabled={issuedBusy || issuedForVersion.length === 0} className="text-[11px] px-2 py-1 border border-zinc-600 text-zinc-200 hover:bg-white/10 disabled:opacity-40" data-testid="as-issued-verify-btn">
                  {issuedBusy ? "…" : "Verify"}
                </button>
                <button type="button" onClick={captureIssued} disabled={issuedBusy} className="text-[11px] px-2 py-1 border border-zinc-600 text-zinc-200 hover:bg-white/10 disabled:opacity-40" data-testid="as-issued-capture-btn">
                  {issuedForVersion.length === 0 ? "Capture" : "Recapture"}
                </button>
              </div>
            </div>
            <p className="text-[10.5px] text-zinc-500 mt-1.5">Byte-exact PDFs stored with a SHA-256 fingerprint at finalisation — provable evidence of exactly what was issued.</p>
            {issuedForVersion.length === 0 ? (
              <p className="text-[10.5px] text-amber-400 mt-2" data-testid="as-issued-empty">No stored copies for this version yet — click Capture.</p>
            ) : (
              <div className="mt-2 space-y-1.5" data-testid="as-issued-list">
                {issuedForVersion.map((a) => {
                  const v = issuedVerify?.results?.find((r) => r.format === a.format);
                  return (
                    <div key={a.format} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`as-issued-row-${a.format}`}>
                      <div className="min-w-0">
                        <div className="text-zinc-200 truncate">{FMT_LABEL[a.format] || a.format}</div>
                        <div className="text-zinc-500 text-[10px] font-mono truncate" title={a.sha256}>
                          {a.stored ? `${Math.round((a.size || 0) / 1024)} KB · ${(a.sha256 || "").slice(0, 12)}…` : (a.error || "not stored")}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {v && (v.integrity_ok
                          ? <BadgeCheck size={14} className="text-emerald-400" data-testid={`as-issued-ok-${a.format}`}/>
                          : <ShieldAlert size={14} className="text-red-400" data-testid={`as-issued-bad-${a.format}`}/>)}
                        <button type="button" onClick={() => downloadIssued(a.format)} disabled={!a.stored} className="px-2 py-1 border border-zinc-600 text-zinc-200 hover:bg-white/10 disabled:opacity-40" data-testid={`as-issued-download-${a.format}`}>
                          <FileDown size={12}/>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {history.length > 0 && (
            <div className="mt-3 text-[11px] text-zinc-400" data-testid="finalise-history">
              <div className="flex items-center gap-1 uppercase tracking-[0.12em] text-[10px] text-zinc-500 mb-1"><History size={11}/> Superseded versions</div>
              {history.map((h, i) => (
                <div key={i}>v{h.snapshot?.version} · archived {fmtDate(h.archived_at)}</div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
